<template>
  <div>
    <navbar/>
    <error-panel showInPopup="false" />

    <main role="main">
        <div class="jumbotron" id="tl-jumbotron">
          <div class="container">
            <div class="row tl-link-block tl-link-block-header pb-1 mb-3">
              <h4 class="col-12 mb-4"><span class="bi bi-cloud-arrow-up-fill text-info mr-2"/>Import Bookmarks</h4>
              <div class="col-12">
                <div class="progress mb-2">
                  <div
                          class="progress-bar"
                          role="progressbar"
                          :style="{ width: stepProgress + '%' }"
                          :aria-valuenow="stepProgress"
                          aria-valuemin="0"
                          aria-valuemax="100">
                    Step {{ currentStep + 1 }} of 5
                  </div>
                </div>
              </div>
            </div>

            <transition name="fade" mode="out-in">
              <div class="row tl-link-block p-4" :key="currentStep">

              <!-- Step 0: Intro -->
              <div class="col-12" v-if="currentStep === 0">
                <p>
                  Bring in your existing bookmarks! We’ll turn your folders into tags.
                  Everything starts out private, and you can change that anytime.
                </p>
                <p>I’ll walk you through it step by step.</p>
                <button class="btn btn-primary" @click="nextStep">Let's do it!</button>
              </div>

              <!-- Step 1: Import type selection -->
              <div class="col-12" v-if="currentStep === 1">
                <p>
                  Where would you like to import from?
                </p>

                <div class="row">
                  <!-- Card 1: Import from my browser -->
                  <div @click="selectBrowserImportType()" class="col-md-6 mb-3">
                    <div class="card card-choice h-100" role="button" tabindex="0" aria-pressed="false" aria-label="Import from my browser">
                      <div class="card-body">
                        <div>
                          <h5 class="card-title mb-1">Import from my browser</h5>
                          <p class="card-text text-muted mb-0">Import bookmarks from one of the supported browsers.</p>
                        </div>

                        <!-- for xs devices -->
                        <div class="icons-row provider-icons ml-3 d-lg-none d-inline-flex" aria-hidden="true">
                          <div class="icon" title="Chrome">
                            <img src="external-icons/chrome_icon.svg"/>
                          </div>
                        </div>

                        <div class="icons-row provider-icons ml-3 d-none d-lg-inline-flex" aria-hidden="true">
                          <div class="icon" title="Chrome">
                            <img src="external-icons/chrome_icon.svg"/>
                          </div>

                          <div class="icon" title="Safari">
                            <img src="external-icons/safari_icon.svg"/>
                          </div>

                          <div class="icon" title="Edge">
                            <img src="external-icons/edge_icon.svg"/>
                          </div>

                          <div class="icon" title="Firefox">
                            <img src="external-icons/firefox_icon.svg"/>
                          </div>

                          <div class="icon" title="Brave">
                            <img src="external-icons/brave_icon.svg"/>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  <!-- Card 2: Import from Pocket -->
                  <div @click="selectPocketImportType()" class="col-md-6 mb-3">
                    <div class="card card-choice h-100" role="button" tabindex="0" aria-pressed="false" aria-label="Import from Pocket">
                      <div class="card-body">
                        <div>
                          <h5 class="card-title mb-1">Import from Pocket</h5>
                          <p class="card-text text-muted mb-0">Bring in saved items from your Pocket account.</p>
                        </div>

                        <div class="ml-3 provider-icons" aria-hidden="true">
                          <div class="icon" title="Pocket">
                              <img src="external-icons/pocket_icon.svg"/>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p class="mt-4 mb-3 text-center">
                  <small class="text-muted">Missing any import options? <a href="mailto:support@justbookmark.it?subject=Missing%20import%20option">Contact us</a></small>
                </p>

              </div>

              <!-- Step 2/A: Browser detection/override -->
              <div class="col-12" v-if="currentStep === 2 && importType === 'browser'">
                <p v-if="detectedBrowser">
                  We detected you're using <strong>{{ browserLabel(detectedBrowser) }}</strong>.
                  Would you like to import bookmarks from this browser?
                </p>

                <div v-if="detectedBrowser">
                  <button
                    class="btn btn-success mr-1" @click="acceptDetectedBrowser">
                    Yes
                  </button>
                  <button
                    class="btn btn-secondary" @click="rejectDetectedBrowser">
                    No, let me select another
                  </button>
                </div>

                <div v-if="showBrowserSelect" class="form-group mt-3">
                  <label for="browser">Select your browser:</label>
                  <select
                    id="browser"
                    class="form-control"
                    v-model="selectedBrowser">
                    <option disabled value="">-- Select --</option>
                    <option v-for="b in browsers" :key="b.value" :value="b.value">
                      {{ b.label }}
                    </option>
                  </select>
                </div>

                <div v-if="selectedBrowser" class="alert alert-info mt-3">
                  <strong>Instructions:</strong>
                  <p v-html="browserInstructions(selectedBrowser)"></p>
                  <small>Save the export file to an easy-to-remember location.</small>
                </div>

                <button v-if="selectedBrowser" class="btn btn-primary mt-3" @click="nextStep"> Got it!</button>
              </div>

              <!-- Step 2/B : Pocket instructions -->
              <div class="col-12" v-if="currentStep === 2 && importType === 'pocket'">
                <div class="alert alert-info mt-3">
                  <strong>Instructions:</strong>
                  <p>To export your bookmarks from pocket, follow the steps at <a href="https://support.mozilla.org/en-US/kb/exporting-your-pocket-list">https://support.mozilla.org/en-US/kb/exporting-your-pocket-list</a>.</p>
                  <p>If it is a <code>.zip</code> file → unzip it → copy the <code>.csv</code> file found inside.</p>
                  <small>Save the export file to an easy-to-remember location. </small>
                </div>

                <button class="btn btn-primary mt-3" @click="nextStep"> Got it!</button>
              </div>

              <!-- Step 3: File upload -->
              <div class="col-12" v-if="currentStep === 3">
                <div class="form-group">
                  <label for="file">Please upload the {{importType === 'browser' ? browserLabel(selectedBrowser) : 'Pocket'}} bookmark file saved in the previous step.
                    It should be a file with <code>{{ importTypeConfigs[importType].extension }}</code> extension.</label>

                  <div><small>Note that the maximum uploadable file size is {{ importTypeConfigs[importType].maxUploadSizeLabel }} (approximately 10.000 bookmarks.)</small></div>
                  <div class="mt-3">
                    <input
                            type="file"
                            id="file"
                            ref="fileInput"
                            class="d-none"
                            :accept="importTypeConfigs[importType].extension"
                    @change="handleFileChange"
                    :disabled="uploadInProgress || uploadCompleted"
                    />
                    <button
                            type="button"
                            class="btn btn-outline-primary"
                            @click="$refs.fileInput.click()"
                            :disabled="uploadInProgress || uploadCompleted">
                    <i class="bi bi-upload mr-2"></i> Choose File
                  </button>

                  <span v-if="file" class="ml-2 text-muted">{{ file.name }}</span>
                </div>
              </div>

                <button
                  class="btn btn-primary mt-3"
                  :disabled="!file || uploadInProgress || uploadCompleted"
                  @click="uploadFile">
                  {{ uploadButtonText }}
                </button>

                <!-- Progress bar -->
                <div v-if="uploadInProgress || uploadCompleted" class="progress mt-3">
                  <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                    :aria-valuenow="uploadProgress"
                    aria-valuemin="0"
                    aria-valuemax="100">
                    {{ uploadProgress }}%
                  </div>
                </div>

                <div v-if="statusMessage" class="mt-3">
                  <div :class="statusClass">{{ statusMessage }}</div>
                </div>
              </div>

              <!-- Step 4: Job polling -->
              <div class="col-12" v-if="currentStep === 4">
                <p v-if="!jobFinished">
                  Upload complete! We’re importing your bookmarks now - this should only take a few seconds.
                </p>
                <Spinner :loaded="jobFinished" />

                <div v-if="jobFinished && !jobFailed" class="pt-4 pb-4 alert alert-success text-center">
                  Import completed successfully. <br/>
                  Your imported bookmarks are ready - feel free to make adjustments if needed.

                  <div class="text-center">
                    <a :href="'/imports/'+importJobId"
                       class="btn btn-primary mt-4"><i class="bi bi-eye mr-2"></i> View imported bookmarks
                    </a>
                  </div>
                </div>

                <div v-if="jobFailed" class="alert alert-danger">
                  <p>Unfortunately, there seems to be some issues with your import. We are on it.
                    Please contact <a href="mailto:support@justbookmark.it">support@justbookmark.it</a>
                    if this doesn't get resolved in 24 hours.
                  </p>
                </div>
              </div>
            </div>
          </transition>
          </div>
        </div>
    </main>
    <tlfooter />
  </div>
</template>

<script>
import navbar from "@/components/NavBar";
import tlfooter from "@/components/Footer";
import ErrorPanel from "@/components/ErrorPanel";
import BackendClient from "@/rest/backend-client";
import axios from "axios";
import Spinner from "@/components/Spinner";
import { UAParser } from 'ua-parser-js';
import BROWSER_CONFIG from '@/util/browsers.js';

export default {
  name: "ImportBookmarksWizard",
  head: {
    title: 'just bookmark it! - import bookmarks'
  },
  components: { ErrorPanel, navbar, tlfooter, Spinner },

  data() {
    return {
      backendClient: new BackendClient(),
      uaParser: new UAParser(),
      currentStep: 0,
      importType: "",
      importTypeConfigs: {
        browser: {
          extension: ".html",
          maxUploadSizeLabel: "10 Mbytes",
          contentLengthRange: "0,10000000"
        },
        pocket: {
          extension: ".csv",
          maxUploadSizeLabel: "2 Mbytes",
          contentLengthRange: "0,2000000"
        }
      },

      selectedBrowser: "",
      detectedBrowser: "",
      showBrowserSelect: false,

      file: null,
      gcsFilePath: null,
      uploadUrl: null,
      uploadInProgress: false,
      uploadCompleted: false,
      uploadProgress: 0,
      statusMessage: "",

      jobFinished: false,
      jobFailed: false,

      importJobId: "",

      pollInterval: null,

      browsers: BROWSER_CONFIG
    };
  },

  mounted() {
    this.detectedBrowser = this.detectBrowser();
    if (this.detectedBrowser === '') {
      this.showBrowserSelect = true;
    }
  },

  computed: {
    stepProgress() {
      return Math.max(23, ((this.currentStep + 1) / 5) * 100);
    },
    uploadButtonText() {
      if (this.uploadCompleted) return "Upload completed";
      if (this.uploadInProgress) return "Uploading...";
      return "Start uploading";
    },
    statusClass() {
      return this.uploadCompleted
        ? "alert alert-success"
        : this.uploadInProgress
        ? "alert alert-warning"
        : "alert alert-info";
    }
  },

  methods: {
    selectBrowserImportType() {
      this.importType = "browser";
      this.currentStep++;
    },

    selectPocketImportType() {
      this.importType = "pocket";
      this.currentStep++;
    },

    nextStep() {
      this.currentStep++;
    },

    acceptDetectedBrowser() {
      this.selectedBrowser = this.detectedBrowser;
    },

    rejectDetectedBrowser() {
      this.showBrowserSelect = true;
      this.selectedBrowser = "";
    },

    detectBrowser() {
      const uaResult = this.uaParser.getResult();
      const isMobile = uaResult?.device?.type === 'mobile';
      const browserName =  uaResult?.browser?.name.toLowerCase().replace(/[\s]/ug, "_").trim();
      const browserValue =  browserName + "_" + (isMobile ? "mobile":"desktop");
      const supportedBrowsers = this.browsers.map(b => b.value);

      if (!supportedBrowsers.includes(browserValue)) {
        return "";
      }
      return browserValue;
    },

    browserLabel(value) {
      const b = this.browsers.find(b => b.value === value);
      return b ? b.label : value;
    },

    browserInstructions(value) {
      const b = this.browsers.find(b => b.value === value);
      return b ? b.instructions : "";
    },

    handleFileChange(event) {
      this.file = event.target.files[0];
      this.statusMessage = "";
      this.uploadProgress = 0;
    },

    async uploadFile() {
      if (!this.file) return;

      this.uploadInProgress = true;
      this.uploadProgress = 0;

      try {
        if (!this.uploadUrl) {
          const importJobType = this.importType === 'browser' ? this.selectedBrowser.toUpperCase() : this.importType.toUpperCase();
          const response = await this.backendClient.getPresignedImportUrl(importJobType);

          this.uploadUrl = response.preSignedUploadUrl;
          this.gcsFilePath = response.filePath;
        }

        this.statusMessage = "Uploading file...";

        const contentLengthRange = this.importTypeConfigs[this.importType].contentLengthRange;

        await axios.put(this.uploadUrl, this.file, {
          headers: { "Content-Type": "text/html", "x-goog-content-length-range": contentLengthRange },
          onUploadProgress: e => {
            if (e.lengthComputable) {
              this.uploadProgress = Math.round((e.loaded * 100) / e.total);
            }
          }
        });

        this.uploadCompleted = true;
        this.statusMessage = "Upload successful!";
        this.uploadUrl = null;

        this.nextStep();
        this.startPolling();
      } catch (err) {
        console.error("Upload failed", err);
        this.statusMessage = "Upload failed. Please try again.";
      } finally {
        this.uploadInProgress = false;
      }
    },

    async pollJobStatus() {
      try {
        const result = await this.backendClient.getJobStatus(this.gcsFilePath);
        if (result.status === "COMPLETED") {
          this.jobFinished = true;
          this.importJobId = result.id;
          clearInterval(this.pollInterval);
        }
        if (result.status === "FAILED") {
          this.jobFinished = true;
          this.jobFailed = true;
        }
      } catch (err) {
        console.error("Polling failed", err);
      }
    },

    startPolling() {
      this.pollInterval = setInterval(this.pollJobStatus, 2000);
    }
  },

  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }
};
</script>
<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.spinner-box {
  margin-top: 2em !important;
}

.provider-icons img,svg { width: 40px; height: 40px; }
.provider-icons .icon { display:inline-flex; align-items:center; justify-content:center; margin-right:.5rem; }
.icons-row { display:flex; align-items:center; }
.sr-only-focusable:focus { position: static; width: auto; height: auto; clip: auto; }
</style>
